import React, { useEffect, useState } from 'react';
import { Key, Plus, Trash2, Copy, CheckCircle, AlertCircle } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { APIKey, APIKeyCreateResponse } from '../../types';
import { getAPIKeys, createAPIKey, revokeAPIKey } from '../../utils/auth/authService';

const APIKeysPage: React.FC = () => {
  const { theme } = useTheme();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<APIKeyCreateResponse | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      const keys = await getAPIKeys();
      setApiKeys(keys);
      setError('');
    } catch (err) {
      setError('Failed to load API keys');
      console.error('Failed to load API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (creating) return;
    
    setCreating(true);
    setError('');
    
    try {
      const result = await createAPIKey({ description: description || undefined });
      setNewKey(result);
      setDescription('');
      await loadAPIKeys();
    } catch (err) {
      setError('Failed to create API key');
      console.error('Failed to create API key:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await revokeAPIKey(keyId);
      await loadAPIKeys();
    } catch (err) {
      setError('Failed to revoke API key');
      console.error('Failed to revoke API key:', err);
    }
  };

  const handleCopyKey = (key: string, keyId: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const closeNewKeyModal = () => {
    setNewKey(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 24, margin: 0, marginBottom: 8 }}>
            API Keys
          </h1>
          <p style={{ color: theme.textSecondary, fontSize: 14, margin: 0 }}>
            Manage API keys for Spark cluster authentication
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 500,
          }}
        >
          <Plus size={18} />
          Create API Key
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* API Keys List */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
      }}>
        {apiKeys.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: theme.textSecondary,
          }}>
            <Key size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p style={{ margin: 0 }}>No API keys yet. Create one to get started.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  KEY ID
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  DESCRIPTION
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  CREATED BY
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  CREATED AT
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  LAST USED
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'left', padding: 12 }}>
                  STATUS
                </th>
                <th style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, textAlign: 'center', padding: 12 }}>
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key, index) => (
                <tr 
                  key={key.key_id}
                  style={{ 
                    borderBottom: index < apiKeys.length - 1 ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  <td style={{ color: theme.text, fontSize: 13, padding: 12, fontFamily: 'monospace' }}>
                    {key.key_id.slice(0, 8)}...
                  </td>
                  <td style={{ color: theme.text, fontSize: 14, padding: 12 }}>
                    {key.description || '-'}
                  </td>
                  <td style={{ color: theme.text, fontSize: 14, padding: 12 }}>
                    {key.created_by}
                  </td>
                  <td style={{ color: theme.text, fontSize: 14, padding: 12 }}>
                    {new Date(key.created_at).toLocaleString()}
                  </td>
                  <td style={{ color: theme.text, fontSize: 14, padding: 12 }}>
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td style={{ padding: 12 }}>
                    {key.revoked ? (
                      <span style={{
                        padding: '4px 8px',
                        background: '#fee',
                        color: '#c33',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Revoked
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        background: '#dcfce7',
                        color: '#15803d',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        Active
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 12, textAlign: 'center' }}>
                    {!key.revoked && (
                      <button
                        onClick={() => handleRevokeKey(key.key_id)}
                        style={{
                          padding: '6px 12px',
                          background: 'transparent',
                          color: '#dc2626',
                          border: `1px solid #dc2626`,
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 13,
                        }}
                        title="Revoke API key"
                      >
                        <Trash2 size={14} />
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: 24,
            width: '90%',
            maxWidth: 500,
          }}>
            <h2 style={{ color: theme.text, fontSize: 20, marginTop: 0, marginBottom: 16 }}>
              Create API Key
            </h2>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ color: theme.text, fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Description (optional)
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Production Spark cluster"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  background: theme.background,
                  color: theme.text,
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setDescription('');
                }}
                disabled={creating}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: creating ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleCreateKey();
                  setShowCreateModal(false);
                }}
                disabled={creating}
                style={{
                  padding: '10px 20px',
                  background: theme.buttonBackground,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: 4,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: creating ? 0.5 : 1,
                }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New API Key Display Modal */}
      {newKey && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: 24,
            width: '90%',
            maxWidth: 600,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <CheckCircle size={24} color="#15803d" />
              <h2 style={{ color: theme.text, fontSize: 20, margin: 0 }}>
                API Key Created Successfully
              </h2>
            </div>
            
            <div style={{
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: 4,
              padding: 12,
              marginBottom: 16,
            }}>
              <p style={{ color: '#92400e', fontSize: 14, margin: 0, fontWeight: 600 }}>
                ⚠️ Important: Copy this API key now. You won't be able to see it again!
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                API KEY
              </label>
              <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}>
                <input
                  type="text"
                  value={newKey.key}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    background: theme.background,
                    color: theme.text,
                    fontSize: 13,
                    fontFamily: 'monospace',
                  }}
                />
                <button
                  onClick={() => handleCopyKey(newKey.key, newKey.key_id)}
                  style={{
                    padding: '10px 16px',
                    background: copiedKeyId === newKey.key_id ? '#dcfce7' : theme.buttonBackground,
                    color: copiedKeyId === newKey.key_id ? '#15803d' : theme.buttonText,
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontWeight: 500,
                  }}
                >
                  {copiedKeyId === newKey.key_id ? (
                    <>
                      <CheckCircle size={16} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {newKey.description && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                  DESCRIPTION
                </label>
                <p style={{ color: theme.text, fontSize: 14, margin: 0 }}>
                  {newKey.description}
                </p>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ color: theme.textSecondary, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>
                KEY ID
              </label>
              <p style={{ color: theme.text, fontSize: 13, margin: 0, fontFamily: 'monospace' }}>
                {newKey.key_id}
              </p>
            </div>

            <button
              onClick={closeNewKeyModal}
              style={{
                width: '100%',
                padding: '10px 20px',
                background: theme.buttonBackground,
                color: theme.buttonText,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysPage;
